import * as z from 'zod';

export const ListCustomFieldSuggestionsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .optional(),
      sorting: z
        .array(
          z.object({
            fieldName: z
              .string()
              .describe('Name of the field to sort by.')
              .max(512)
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .optional(),
});
export const ListCustomFieldSuggestionsResponse = z.object({
  suggestions: z
    .array(
      z.object({
        name: z
          .string()
          .describe('Custom field name.')
          .max(150)
          .optional()
          .nullable(),
        defaultPrivacy: z
          .enum(['UNKNOWN', 'PUBLIC', 'PRIVATE'])
          .describe('Privacy level of the custom field.')
          .optional(),
        fieldType: z
          .enum(['UNKNOWN', 'TEXT', 'NUMBER', 'DATE', 'URL', 'SOCIAL'])
          .describe('Type of information to provide for members.')
          .optional(),
        socialType: z
          .enum([
            'UNKNOWN',
            'FACEBOOK',
            'INSTAGRAM',
            'LINKEDIN',
            'TWITTER',
            'YOUTUBE',
            'PINTEREST',
            'TIKTOK',
            'DEVIANTART',
            'SOUNDCLOUD',
            'TUMBLR',
            'VIMEO',
            'VKONTAKTE',
            'ODNOKLASSNIKI',
            'OTHER',
          ])
          .describe('Social network type.')
          .optional(),
        fieldOrigin: z
          .enum(['UNKNOWN', 'CUSTOM', 'CONTACT', 'SYSTEM'])
          .describe('Field origin.')
          .optional(),
        customFieldId: z
          .string()
          .describe('Custom field ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const QueryCustomFieldSuggestionsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          fieldType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['name', 'fieldType']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.'
    ),
});
export const QueryCustomFieldSuggestionsResponse = z.object({
  suggestions: z
    .array(
      z.object({
        name: z
          .string()
          .describe('Custom field name.')
          .max(150)
          .optional()
          .nullable(),
        defaultPrivacy: z
          .enum(['UNKNOWN', 'PUBLIC', 'PRIVATE'])
          .describe('Privacy level of the custom field.')
          .optional(),
        fieldType: z
          .enum(['UNKNOWN', 'TEXT', 'NUMBER', 'DATE', 'URL', 'SOCIAL'])
          .describe('Type of information to provide for members.')
          .optional(),
        socialType: z
          .enum([
            'UNKNOWN',
            'FACEBOOK',
            'INSTAGRAM',
            'LINKEDIN',
            'TWITTER',
            'YOUTUBE',
            'PINTEREST',
            'TIKTOK',
            'DEVIANTART',
            'SOUNDCLOUD',
            'TUMBLR',
            'VIMEO',
            'VKONTAKTE',
            'ODNOKLASSNIKI',
            'OTHER',
          ])
          .describe('Social network type.')
          .optional(),
        fieldOrigin: z
          .enum(['UNKNOWN', 'CUSTOM', 'CONTACT', 'SYSTEM'])
          .describe('Field origin.')
          .optional(),
        customFieldId: z
          .string()
          .describe('Custom field ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
